import React, { useEffect, useState, useRef, useCallback } from "react";
import Sparks from "../../assets/Sparks.svg";
import { ButtonCustom } from "../input/ButtonCustom";
import RadiologistApi from "../../services/ApiServices/RadiologistApi";
import PDFPreview from "./PDFPreview";
import { useViewer } from "../../view/app/DicomViewerNew/context/ViewerContext";
import { buildSnapshotsOnce } from "../../view/app/DicomViewerNew/utils/snapShots_l";
import { exportPreviewToPDF } from "../../utils/pdfHelper";

const sections = [
  { key: "lungsAndAirways", label: "Lungs and airways" },
  { key: "pleura", label: "Pleura" },
  { key: "abdomen", label: "Abdomen" },
  { key: "bones", label: "Bones" },
  { key: "heartAndBloodVessels", label: "Heart and blood vessels" },
  { key: "lymphNodules", label: "Lymph nodules" },
  { key: "lungNodules", label: "Lung Nodules" },
  { key: "impression", label: "Impression" },
];

const Report = ({
  stage = "loading",
  setStage,
  closePopUp,
  accessionNumber,
  currentUser,
}) => {
  const [formData, setFormData] = useState(
    sections.reduce((acc, s) => ({ ...acc, [s.key]: "" }), {})
  );

  const { parsedDicomData, annotationsData, imageIdCTX, meta, examData } =
    useViewer();

  const [snapshots, setSnapshots] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [genErrors, setGenErrors] = useState([]);
  const previewRef = useRef(null);

  useEffect(() => {
    const fetchOrCreateReport = async () => {
      try {
        const { data } = await RadiologistApi.getReport(accessionNumber);

        if (data) {
          setFormData((prev) => ({ ...prev, ...data }));
          setStage("edit");
          return;
        }
      } catch {
        // Report not found — create a new one
      }

      try {
        await RadiologistApi.createReport(accessionNumber, {
          examId: accessionNumber,
          createdBy: currentUser?.profile?.UserID,
          isSigned: false,
        });
        setStage("edit");
      } catch (err) {
        console.error("Failed to create initial report:", err);
      }
    };

    if (stage === "loading" && accessionNumber) {
      fetchOrCreateReport();
    }
  }, [stage, accessionNumber, currentUser, setStage]);

  // Generate CT+overlay snapshots once when we enter the preview
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (stage !== "success") return;

      const imageIdsOK = Array.isArray(imageIdCTX) && imageIdCTX.length > 0;
      const metaOK = !!meta;
      const examDataOK = !!(examData && examData.jsonData);
      const annOK = !!annotationsData;

      // If any prerequisite missing, show why
      if (!imageIdsOK || !metaOK || !examDataOK || !annOK) {
        const errs = [];
        if (!imageIdsOK) errs.push("imageIdCTX missing from context");
        if (!metaOK) errs.push("meta missing from context");
        if (!examDataOK) errs.push("examData.jsonData missing from context");
        if (!annOK) errs.push("annotationsData missing from context");

        if (!cancelled) {
          setSnapshots([]);
          setGenErrors(errs);
          setIsGenerating(false);
        }
        return;
      }

      const findings = Object.values(annotationsData || {})
        .flat()
        .filter((a) => a.creator === "Genesis AI" && a.type === "finding");

      if (findings.length === 0) {
        if (!cancelled) {
          setSnapshots([]);
          setGenErrors(['No "Genesis AI" findings in annotationsData.']);
          setIsGenerating(false);
        }
        return;
      }

      setIsGenerating(true);
      const { snapshots: snps, errors } = await buildSnapshotsOnce({
        imageIds: imageIdCTX, // ⬅️ pass context stack here
        meta,
        examData,
        findings,
        size: { width: 1024, height: 1024 },
        maxWidth: 640,
      });

      if (!cancelled) {
        setSnapshots(snps);
        setGenErrors(errors || []);
        setIsGenerating(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [stage, imageIdCTX, meta, examData, annotationsData, parsedDicomData]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (isSigned = false) => {
    try {
      await RadiologistApi.updateReport(accessionNumber, {
        ...formData,
        isSigned,
        examId: accessionNumber,
        createdBy: currentUser?.profile?.UserID,
      });

      if (isSigned) {
        setStage("success"); // show the preview
      } else {
        closePopUp(); // just close if saving (not signing)
      }
    } catch (err) {
      console.error("Failed to save report:", err);
    }
  };

  const handleExport = useCallback(() => {
    if (!previewRef.current) return;
    exportPreviewToPDF(
      previewRef.current,
      `report-${accessionNumber || "study"}.pdf`
    );
  }, [accessionNumber]);

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 flex items-center justify-center p-[10px]">
      <div className="bg-[#2E2E2E] rounded-md p-8 pt-10 relative text-white font-Inter shadow-xl w-[800px] h-[1080px] max-w-[calc(100vw-20px)] max-h-[calc(100vh-20px)] box-border flex flex-col">
        <button
          onClick={closePopUp}
          className="absolute top-4 right-4 text-white text-xl">
          X
        </button>

        {stage === "loading" && (
          <div className="flex flex-col items-center justify-center h-full w-full">
            <img
              src={Sparks}
              alt="Spark"
              className="w-[64px] h-[73px] mb-6 animate-pulse"
            />
            <p className="text-sm text-center">
              The system is creating the report.
              <br />
              Please wait
            </p>
          </div>
        )}

        {stage === "edit" && (
          <>
            <h2 className="text-[18px] font-semibold flex items-center gap-[10px] mb-6">
              <img src={Sparks} alt="Spark" className="w-[18px] h-[18px]" />
              Findings
            </h2>

            <div className="flex-1 overflow-y-auto pr-1">
              <form className="flex flex-col gap-6">
                {sections.map(({ key, label }) => (
                  <div key={key}>
                    <label className="block mb-2 text-sm font-medium">
                      {label}
                    </label>
                    <textarea
                      className="w-full bg-[#0000004D] text-white text-sm rounded-lg p-3 h-16 resize-none focus:outline-none"
                      placeholder="Add your text here"
                      value={formData[key]}
                      onChange={(e) => handleChange(key, e.target.value)}
                    />
                  </div>
                ))}
              </form>
            </div>

            <div className="pt-6 mt-6 border-t border-[#444] flex gap-4">
              <ButtonCustom
                label="Save Report"
                width="316px"
                height="50px"
                backgroundColor="transparent"
                textColor="white"
                borderRadius="8px"
                fontSize="14px"
                fontWeight="500"
                containerClassName="w-full border border-white"
                onClick={() => handleSave(false)}
                border="1px solid white"
              />
              <ButtonCustom
                label="Sign Report"
                width="316px"
                height="50px"
                backgroundColor="#6A5BFF"
                textColor="white"
                borderRadius="8px"
                fontSize="14px"
                fontWeight="500"
                containerClassName="w-full"
                svgIcon={<img src={Sparks} alt="Spark" className="w-4 h-4" />}
                onClick={() => handleSave(true)}
              />
            </div>
          </>
        )}

        {stage === "success" && (
          <div className="flex h-full flex-col">
            {/* Scrollable content area */}
            <div className="flex-1 min-h-0 overflow-y-hidden overflow-x-hidden flex items-start justify-center">
              <div
                ref={previewRef}
                className="bg-white rounded shadow overflow-y-auto overflow-x-hidden my-4"
                style={{
                  width: "794px",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  boxSizing: "border-box",
                  padding: 0,
                }}>
                <PDFPreview
                  reportData={{
                    examMeta: parsedDicomData,
                    annotationsData,
                    snapshots,
                    isGeneratingSnapshots: isGenerating,
                    snapshotErrors: genErrors,
                    reportFileds: formData,
                  }}
                />
              </div>
            </div>

            {/* Sticky footer (always visible) */}
            <div className="sticky bottom-0 left-0 right-0 bg-[#2E2E2E] pt-6 mt-2 border-t border-[#444] flex gap-4">
              <ButtonCustom
                label="Export PDF"
                width="316px"
                height="50px"
                backgroundColor="#6A5BFF"
                textColor="white"
                borderRadius="8px"
                fontSize="14px"
                fontWeight="500"
                containerClassName="w-full"
                svgIcon={<img src={Sparks} alt="Spark" className="w-4 h-4" />}
                onClick={handleExport}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Report;
